/**
 * Explorer links. The explorer comes from the descriptor, never spelled here; the
 * external-link glyph sets a leaving link apart from in-app navigation.
 */

import { shortHash } from "../lib/format";
import { getExplorerAddressUrl, getExplorerTxUrl } from "../lib/runtimeConfig";

/** Small "opens in a new tab" glyph; decorative, so hidden from the a11y tree. */
export function ExternalLinkGlyph() {
  return (
    <svg
      className="external-glyph"
      width="11"
      height="11"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4.5 1.5h6v6" />
      <path d="M10.5 1.5L5 7" />
      <path d="M9 7.5v3h-7.5V3h3" />
    </svg>
  );
}

/** Transaction hash → explorer. Callers must not render this without a hash. */
export function TxLink({ txHash, label }: { txHash: string; label?: string }) {
  const href = getExplorerTxUrl(txHash);
  const text = label ?? shortHash(txHash);
  if (href === null) {
    return (
      <span className="explorer-link-hash" title={txHash}>
        {text}
      </span>
    );
  }
  return (
    <a className="explorer-link" href={href} target="_blank" rel="noopener noreferrer" title={txHash}>
      <span className="explorer-link-hash">{text}</span>
      <ExternalLinkGlyph />
    </a>
  );
}

/** A transaction inside a sentence (a banner): no glyph, and its short hash alone without an explorer. */
export function InlineTxLink({ txHash }: { txHash: string }) {
  const href = getExplorerTxUrl(txHash);
  if (href === null) {
    return (
      <span className="explorer-link-hash" title={txHash}>
        {shortHash(txHash)}
      </span>
    );
  }
  return (
    <a className="explorer-link" href={href} target="_blank" rel="noopener noreferrer" title={txHash}>
      <span className="explorer-link-hash">{shortHash(txHash)}</span>
    </a>
  );
}

/** Account address → explorer (e.g. the taker that settled a batch). */
export function AddressLink({ address }: { address: string }) {
  const href = getExplorerAddressUrl(address);
  if (href === null) {
    return (
      <span className="explorer-link-hash" title={address}>
        {shortHash(address)}
      </span>
    );
  }
  return (
    <a className="explorer-link" href={href} target="_blank" rel="noopener noreferrer" title={address}>
      <span className="explorer-link-hash">{shortHash(address)}</span>
      <ExternalLinkGlyph />
    </a>
  );
}
